/**
 * One account, read once for all its tabs (Customer 360 Phase A,
 * docs/product/customer-360/01 §10): the account and its first page of history
 * and cheques, the statement TallyPrime's bills make (with the company's rule
 * for bills with no due date), how current the figures are, and — for people
 * who may read the receivables data, and nobody else — whether an accounting
 * check puts the balance in doubt. Everything the page says is worked out from
 * these by the domain's one set of words (`semantics.ts`), never by a screen.
 *
 * Nothing here decides who may see the account: `customers`, `customer_activity`,
 * `pdc_cheques` and `source_account_detail` are each refused by the database
 * for anybody outside it.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
    BALANCE_REVIEW_CHECKS, accountAttention, accountOverdue, accountTimeline, agoWords, balanceReviewWords, bookRow, emptyDraft, holds,
    openPromises, sourceSignal,
    type ActivityEntry, type Attention, type BookRow, type Cheque, type Customer, type FollowUpDraft, type OverdueAnswer, type PriorityReason,
    type TenantContext, type TimelineEvent,
} from '@timely/domain';
import { supabase } from '../../lib/supabaseClient';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { useFormatters } from '../../book/useFormatters';
import { loadMoreActivity, useMemberNames } from '../../book/useBook';
import { priorityReasons } from '../../book/usePriorityReasons';
import { isTallyKey } from '../../book/accountCode';
import { useAccountStatement, type AccountStatementState } from '../../receivables/AccountStatement';
import { useReceivablesFreshness, type FreshnessReading } from '../../receivables/useReceivablesFreshness';
import type { StoredFinding } from '../../receivables/useSourceFacts';

export interface AccountDetailData {
    customer: Customer;
    updatedAt: string;
    activity: ActivityEntry[];
    cheques: Cheque[];
    hasMoreActivity: boolean;
}

/** What a person may do on this account, from their capabilities: each control shows only for somebody who may use it. */
export interface AccountRights {
    record: boolean;
    /** May use Call and Email: working the account, so `mayContact`. A Viewer still reads the numbers and addresses, as text. */
    contact: boolean;
    edit: boolean;
    assign: boolean;
    seeCheques: boolean;
    recordCheque: boolean;
    /** May read the receivables data: findings, source events, connector status (tier 2). */
    readSourceData: boolean;
    /** May change it: choose the rule for bills with no due date, acknowledge a finding. */
    manageSourceData: boolean;
    useAi: boolean;
}

export interface AccountPage {
    customer: Customer;
    updatedAt: string;
    row: BookRow;
    source: 'tally' | 'sheet';
    statement: AccountStatementState;
    /** TallyPrime's bills for the account are still being read: what depends on them waits, and says so. */
    statementLoading: boolean;
    overdue: OverdueAnswer;
    /** The reasons engine's reasons (Today's words); null until the company's settings are read. */
    reasons: PriorityReason[] | null;
    attention: Attention;
    freshness: FreshnessReading;
    /** The whole loaded history, newest first, as one timeline. */
    timeline: TimelineEvent[];
    entries: ActivityEntry[];
    openPromises: ActivityEntry[];
    cheques: Cheque[];
    moreHistory: boolean;
    loadOlder: () => Promise<void>;
    loadingOlder: boolean;
    names: Map<string, string>;
    rights: AccountRights;
    /** Read the account again after a change, and say what happened. */
    changed: (message: string) => Promise<void>;
    failed: (message: string) => void;
    notice: { tone: 'ok' | 'error'; text: string } | null;
    clearNotice: () => void;
    /** A save is under way: controls wait. */
    busy: boolean;
    setBusy: (busy: boolean) => void;
    /** The page's three actions, wherever they are pressed from. */
    openRecord: () => void;
    openRemind: () => void;
    openCheque: () => void;
    /**
     * The half-written outcome, held by the account rather than by a form: the rail's composer, the dialog and the
     * phone's sheet are views of one draft, so a layout change, going back to the activity, or closing the sheet never
     * loses what somebody was writing.
     */
    record: {
        draft: FollowUpDraft;
        setDraft: Dispatch<SetStateAction<FollowUpDraft>>;
        /** "Other" chosen for the promised amount: the amount is typed. */
        other: boolean;
        setOther: (other: boolean) => void;
        /** The composer is open in the account's activity: in the rail, or above the whole history. */
        composing: boolean;
        compose: (on: boolean) => void;
    };
}

const AccountContext = createContext<AccountPage | null>(null);

export const AccountProvider = AccountContext.Provider;

export function useAccount(): AccountPage {
    const page = useContext(AccountContext);
    if (!page) throw new Error('useAccount outside an account');
    return page;
}

/**
 * Whether an open accounting check on this account makes its balance uncertain — read only for somebody who may read
 * the receivables data (the database refuses anybody else anyway, and the page never asks for them: 01 §6.3).
 */
function useBalanceReview(workspaceId: string, customerId: string, holder: boolean): boolean {
    const [found, setFound] = useState(false);
    useEffect(() => {
        if (!holder) { setFound(false); return; }
        let current = true;
        void supabase().rpc('source_findings_list', { p_workspace: workspaceId, p_include_resolved: false })
            .then(({ data, error }) => {
                if (!current) return;
                const findings = (!error && Array.isArray(data) ? data : []) as StoredFinding[];
                setFound(findings.some(f => f.customerId === customerId && !f.resolvedAt && BALANCE_REVIEW_CHECKS.includes(f.code)));
            });
        return () => { current = false; };
    }, [workspaceId, customerId, holder]);
    return found;
}

/**
 * Reaching the customer — Call and Email — is working the account, not reading it (owner, 24 Oct 2026): it needs
 * "Record follow-ups", like recording what came of it, everywhere it is offered. Somebody who may only read sees the
 * numbers and addresses as text, and keeps the app's own phone bar.
 */
export const mayContact = (context: TenantContext): boolean => holds(context, 'followups.record');

/** Everything the account's page shows, from the loaded account. */
export function useAccountPage(detail: AccountDetailData, onReload: () => Promise<void>, actions: Pick<AccountPage, 'openRecord' | 'openRemind' | 'openCheque'>): AccountPage {
    const context = useActiveWorkspace();
    const { money, date, today } = useFormatters();
    const workspaceId = context.workspace.id;
    const { customer, updatedAt, cheques } = detail;

    const rights: AccountRights = useMemo(() => ({
        record: holds(context, 'followups.record'),
        contact: mayContact(context),
        edit: holds(context, 'customers.edit'),
        // Reassigning needs both: the row policy asks for edit, the column guard for assign.
        assign: holds(context, 'customers.edit') && holds(context, 'customers.assign'),
        seeCheques: holds(context, 'cheques.view'),
        recordCheque: holds(context, 'cheques.manage'),
        readSourceData: holds(context, 'datasource.view'),
        manageSourceData: holds(context, 'datasource.manage'),
        useAi: holds(context, 'ai.generate'),
    }), [context]);

    const [entries, setEntries] = useState<ActivityEntry[]>(detail.activity);
    const [moreHistory, setMoreHistory] = useState(detail.hasMoreActivity);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [notice, setNotice] = useState<AccountPage['notice']>(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<FollowUpDraft>(() => emptyDraft('note'));
    const [other, setOther] = useState(false);
    const [composing, compose] = useState(false);
    // A reload brings the newest page of history back: anything older loaded before is read again on request.
    useEffect(() => { setEntries(detail.activity); setMoreHistory(detail.hasMoreActivity); }, [detail.activity, detail.hasMoreActivity]);

    const statement = useAccountStatement(workspaceId, customer.id);
    const freshness = useReceivablesFreshness(workspaceId);
    const balanceReview = useBalanceReview(workspaceId, customer.id, rights.readSourceData);
    const names = useMemberNames(workspaceId);

    const row = useMemo(() => bookRow(customer, today), [customer, today]);
    const source: 'tally' | 'sheet' = isTallyKey(customer.sourceKey) ? 'tally' : 'sheet';
    const collection = statement.settings.collection;
    // The company's thresholds, or no reasons at all: never reasons stated against thresholds that are not its own.
    const reasons = useMemo(
        () => (statement.settingsReady ? priorityReasons(row, collection, rights.seeCheques ? cheques : [], { date, today }, money.exact) : null),
        [statement.settingsReady, row, collection, rights.seeCheques, cheques, date, today, money],
    );

    const overdue = useMemo(() => accountOverdue({
        customer, source, statement: source === 'tally' ? statement.statement : null, rule: statement.rule,
        graceDays: statement.settings.graceDays, criticalDays: collection.criticalOverdueDays,
    }), [customer, source, statement.statement, statement.rule, statement.settings.graceDays, collection.criticalOverdueDays]);

    const f = freshness.freshness;
    const nilBalanceBillsOpen = overdue.kind === 'settled' && overdue.openBills;
    const total = Math.abs(Number(customer.total) || 0);
    const overLimit = customer.creditLimit && customer.totalType !== 'Cr' && total > customer.creditLimit
        ? `${money.exact(total)} owed against a credit limit of ${money.exact(customer.creditLimit)}` : null;
    const attention = useMemo(() => accountAttention({
        source: sourceSignal(f, { holder: rights.readSourceData, age: f && f.minutesSinceUpdate !== null ? agoWords(f.minutesSinceUpdate) : null }),
        balanceReview: balanceReview ? balanceReviewWords(rights.readSourceData) : null,
        reasons: reasons ?? [],
        overCreditLimit: overLimit,
        nilBalanceBillsOpen,
    }), [f, rights.readSourceData, balanceReview, reasons, overLimit, nilBalanceBillsOpen]);

    const timeline = useMemo(() => accountTimeline({
        entries,
        cheques: rights.seeCheques ? cheques : [],
        cleared: source === 'tally' && statement.statement?.billWise ? statement.statement.cleared : [],
        dayOf: at => date.inputValue(at),
        short: d => date.short(d),
        today,
    }), [entries, rights.seeCheques, cheques, source, statement.statement, date, today]);

    const loadOlder = useCallback(async () => {
        setLoadingOlder(true);
        try {
            const more = await loadMoreActivity(workspaceId, customer.id, entries.length);
            setEntries(list => [...list, ...more.entries]);
            setMoreHistory(more.hasMore);
        } catch (error) {
            setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'We could not load any more of this history.' });
        } finally {
            setLoadingOlder(false);
        }
    }, [workspaceId, customer.id, entries.length]);

    const changed = useCallback(async (message: string) => {
        await onReload();
        await statement.detail.reload();
        setNotice({ tone: 'ok', text: message });
    }, [onReload, statement.detail]);

    return {
        customer, updatedAt, row, source, statement, statementLoading: source === 'tally' && statement.loading,
        overdue, reasons, attention, freshness, timeline, entries,
        openPromises: openPromises(entries), cheques, moreHistory, loadOlder, loadingOlder, names, rights,
        changed, failed: text => setNotice({ tone: 'error', text }), notice, clearNotice: () => setNotice(null),
        busy, setBusy, ...actions, record: { draft, setDraft, other, setOther, composing, compose },
    };
}

/** Somebody has started an outcome: anything chosen or written that the empty form does not have. */
export function draftStarted(draft: FollowUpDraft): boolean {
    const empty = emptyDraft('note');
    return draft.kind !== empty.kind || draft.body.trim() !== '' || draft.promisedAmount !== '' || draft.promisedOn !== ''
        || draft.nextFollowUp !== '' || draft.clearFollowUp || draft.completed;
}
